// @ts-nocheck
/**
 * FERP Workforce - Repository Factory
 *
 * Creates type-safe repositories with automatic:
 * - Tenant ID injection from JWT claim
 * - Audit logging for all operations
 * - Optimistic updates for mutations
 * - Cache invalidation strategies
 *
 * CONSTRAINTS:
 * - All data access through repositories (NO direct useQuery)
 * - Tenant ID from JWT only (SINGLE SOURCE OF TRUTH)
 * - All operations logged for audit trail
 *
 * NOTE: Uses `string` for entity IDs instead of portal's `BackendId`
 * branded type, since workforce does not use branded IDs.
 */


import { useCallback, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports -- Repository factory requires direct Apollo access
import { useQuery, useMutation, useApolloClient } from '@apollo/client/react';
import { type DocumentNode, type TypedDocumentNode, type QueryOptions, type MutationOptions, type OperationVariables, type ApolloCache, type FetchResult,  } from '@apollo/client';

import { useTenantId } from './useTenantId';
import { useAuditLogger, type AuditOperation } from './useAuditLogger';
import { MOCK_DATA_MAP } from '../data/mockData';

/**
 * Check if mock data should be used at runtime.
 * This is evaluated dynamically so it can detect window.__ENV__ that's set after module load.
 */
function shouldUseMockData(): boolean {
  // Only use mock data in development
  if (!import.meta.env.DEV) {
    return false;
  }

  try {
    if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).__ENV__) {
      const env = (window as unknown as Record<string, unknown>).__ENV__ as Record<string, string>;
      return env.ENABLE_MOCK_AUTH === 'true';
    }
  } catch { /* fall through */ }

  return false;
}

/**
 * Safe tenant ID hook that works in mock mode (without AuthProvider)
 * In mock mode, returns a dummy tenant ID without calling useTenantId()
 */
function useSafeTenantId(): string | null {
  // In mock mode, use a placeholder tenant ID that doesn't require auth context
  if (shouldUseMockData()) {
    return 'mock-tenant-dev';
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useTenantId();
}

/**
 * Base entity interface - all entities must have an id
 */
export interface BaseEntity {
  id: string;
  __typename?: string;
}

/**
 * Repository configuration
 */
export interface RepositoryConfig<
  TEntity extends BaseEntity,
  TListData = { items: TEntity[] },
  TGetData = TEntity,
  TCreateInput = Partial<TEntity>,
  TUpdateInput = Partial<TEntity>,
> {
  /** Entity name for logging and cache management */
  entityName: string;

  /** GraphQL query for listing entities */
  listQuery: DocumentNode | TypedDocumentNode<TListData, OperationVariables>;

  /** GraphQL query for getting a single entity */
  getQuery: DocumentNode | TypedDocumentNode<TGetData, OperationVariables>;

  /** GraphQL mutation for creating an entity */
  createMutation?: DocumentNode | TypedDocumentNode<TEntity, OperationVariables>;

  /** GraphQL mutation for updating an entity */
  updateMutation?: DocumentNode | TypedDocumentNode<TEntity, OperationVariables>;

  /** GraphQL mutation for deleting an entity */
  deleteMutation?: DocumentNode | TypedDocumentNode<{ success: boolean }, OperationVariables>;

  /** Extract items from list query result */
  extractList?: (data: TListData) => TEntity[];

  /** Extract entity from get query result */
  extractEntity?: (data: TGetData) => TEntity;

  /** ID field name in variables (defaults to `${entityName.toLowerCase()}Id`) */
  idFieldName?: string;

  /** Enable optimistic updates for mutations */
  optimisticUpdates?: boolean;

  /** Cache invalidation strategy */
  cacheInvalidation?: 'refetch' | 'update' | 'evict';
}

/**
 * Repository hook return type
 */
export interface Repository<
  TEntity extends BaseEntity,
  TListData = { items: TEntity[] },
  TGetData = TEntity,
  TCreateInput = Partial<TEntity>,
  TUpdateInput = Partial<TEntity>,
> {
  /** List all entities for the current tenant */
  useList: (
    variables?: Record<string, unknown>,
    options?: Omit<QueryOptions<TListData, OperationVariables>, 'variables'>
  ) => {
    data: TEntity[];
    loading: boolean;
    error: Error | undefined;
    refetch: () => void;
  };

  /** Get a single entity by ID */
  useGet: (
    id: string | null | undefined,
    options?: Omit<QueryOptions<TGetData, OperationVariables>, 'variables' | 'skip'>
  ) => {
    data: TEntity | null;
    loading: boolean;
    error: Error | undefined;
    refetch: () => void;
  };

  /** Create a new entity */
  useCreate: () => {
    create: (input: TCreateInput) => Promise<TEntity>;
    loading: boolean;
    error: Error | undefined;
  };

  /** Update an existing entity */
  useUpdate: () => {
    update: (id: string, input: TUpdateInput) => Promise<TEntity>;
    loading: boolean;
    error: Error | undefined;
  };

  /** Delete an entity */
  useDelete: () => {
    delete: (id: string) => Promise<boolean>;
    loading: boolean;
    error: Error | undefined;
  };

  /** Entity name (for display purposes) */
  entityName: string;
}

/**
 * Create a repository hook for an entity type
 */
export function createRepository<
  TEntity extends BaseEntity,
  TListData = { items: TEntity[] },
  TGetData = TEntity,
  TCreateInput = Partial<TEntity>,
  TUpdateInput = Partial<TEntity>,
>(
  config: RepositoryConfig<TEntity, TListData, TGetData, TCreateInput, TUpdateInput>
): () => Repository<TEntity, TListData, TGetData, TCreateInput, TUpdateInput> {
  const {
    entityName,
    listQuery,
    getQuery,
    createMutation,
    updateMutation,
    deleteMutation,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extractList = (data: any) => data?.items || data?.[`${entityName.toLowerCase()}s`] || [],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extractEntity = (data: any) => data?.[entityName.toLowerCase()] || data,
    idFieldName = `${entityName.toLowerCase()}Id`,
    optimisticUpdates = true,
    cacheInvalidation = 'update',
  } = config;

  return function useRepository(): Repository<
    TEntity,
    TListData,
    TGetData,
    TCreateInput,
    TUpdateInput
  > {
    // Use safe tenant ID that works in mock mode without AuthProvider
    const tenantId = useSafeTenantId();
    const client = useApolloClient();
    const logger = useAuditLogger(entityName);

    /**
     * List hook with tenant injection
     */
    const useList = useCallback(
      (
        variables?: Record<string, unknown>,
        options?: Omit<QueryOptions<TListData, OperationVariables>, 'variables'>
      ) => {
        // In mock mode, return mock data directly without Apollo
        if (shouldUseMockData()) {
          const mockItems = (MOCK_DATA_MAP[entityName] || []) as TEntity[];
          return {
            data: mockItems,
            loading: false,
            error: undefined,
            refetch: () => { /* no-op in mock mode */ },
          };
        }

        const trace = logger.trace('list', variables);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const result = useQuery<TListData>(listQuery, {
          ...options,
          variables: { tenantId, ...variables },
          skip: !tenantId,
          onCompleted: (data: any) => {
            trace.success();
            options?.onCompleted?.(data);
          },
          onError: (error: any) => {
            trace.failure(error);
            options?.onError?.(error);
          },
        });

        return {
          data: result.data ? extractList(result.data) : [],
          loading: result.loading,
          error: result.error,
          refetch: result.refetch,
        };
      },
      [tenantId, logger]
    );

    /**
     * Get hook with tenant injection
     */
    const useGet = useCallback(
      (
        id: string | null | undefined,
        options?: Omit<QueryOptions<TGetData, OperationVariables>, 'variables' | 'skip'>
      ) => {
        // In mock mode, return mock entity by ID
        if (shouldUseMockData()) {
          const mockItems = (MOCK_DATA_MAP[entityName] || []) as TEntity[];
          const found = id ? mockItems.find((item) => item.id === id) || mockItems[0] || null : null;
          return {
            data: found,
            loading: false,
            error: undefined,
            refetch: () => { /* no-op in mock mode */ },
          };
        }

        const trace = logger.trace('get', { id });

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const result = useQuery<TGetData>(getQuery, {
          ...options,
          variables: { tenantId, [idFieldName]: id },
          skip: !tenantId || !id,
          onCompleted: (data: any) => {
            trace.success(id || undefined);
            options?.onCompleted?.(data);
          },
          onError: (error: any) => {
            trace.failure(error, id || undefined);
            options?.onError?.(error);
          },
        });

        return {
          data: result.data ? extractEntity(result.data) : null,
          loading: result.loading,
          error: result.error,
          refetch: result.refetch,
        };
      },
      [tenantId, logger, idFieldName]
    );

    /**
     * Create mutation with tenant injection
     */
    const useCreate = useCallback(() => {
      if (shouldUseMockData()) {
        return {
          create: async (input: TCreateInput) => {
            await new Promise((r) => setTimeout(r, 300));
            return { id: `mock-${Date.now()}`, ...input } as unknown as TEntity;
          },
          loading: false,
          error: undefined,
        };
      }

      if (!createMutation) {
        return {
          create: async () => {
            throw new Error(`Create mutation not configured for ${entityName}`);
          },
          loading: false,
          error: undefined,
        };
      }

      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [mutate, { loading, error }] = useMutation<TEntity>(createMutation);

      const create = async (input: TCreateInput): Promise<TEntity> => {
        const trace = logger.trace('create', { input: input as Record<string, unknown> });

        try {
          const result = await mutate({
            variables: { tenantId, input },
            update: (cache, { data }) => {
              if (cacheInvalidation === 'evict') {
                // Evict all queries for this entity type
                cache.evict({ fieldName: `${entityName.toLowerCase()}s` });
                cache.gc();
              } else if (cacheInvalidation === 'update' && data) {
                // Update strategy: Add new entity to existing list cache
                try {
                  const existingData = cache.readQuery<TListData>({
                    query: listQuery,
                    variables: { tenantId },
                  });

                  if (existingData) {
                    // Cast to TListData for compatibility with extractList
                    const existingItems = extractList(existingData as unknown as TListData);
                    const newEntity = data as TEntity;

                    // Prepend new entity to the list
                    const updatedItems = [newEntity, ...existingItems];

                    // Write back to cache with updated list - use any cast for Apollo type compatibility
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    cache.writeQuery<any>({
                      query: listQuery,
                      variables: { tenantId },
                      data: {
                        ...existingData,
                        items: updatedItems,
                        [`${entityName.toLowerCase()}s`]: updatedItems,
                      },
                    });
                  }
                } catch {
                  // Cache miss - no existing data to update, that's OK
                }
              }
              // 'refetch' strategy is handled by Apollo's refetchQueries option
            },
          });

          const entity = result.data as TEntity;
          trace.success(entity?.id);
          return entity;
        } catch (err) {
          trace.failure(err as Error);
          throw err;
        }
      };

      return { create, loading, error };
    }, [tenantId, logger, cacheInvalidation]);

    /**
     * Update mutation with tenant injection
     */
    const useUpdate = useCallback(() => {
      if (shouldUseMockData()) {
        return {
          update: async (id: string, input: TUpdateInput) => {
            await new Promise((r) => setTimeout(r, 300));
            return { id, ...input } as unknown as TEntity;
          },
          loading: false,
          error: undefined,
        };
      }

      if (!updateMutation) {
        return {
          update: async () => {
            throw new Error(`Update mutation not configured for ${entityName}`);
          },
          loading: false,
          error: undefined,
        };
      }

      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [mutate, { loading, error }] = useMutation<TEntity>(updateMutation);

      const update = async (id: string, input: TUpdateInput): Promise<TEntity> => {
        const trace = logger.trace('update', { id, input: input as Record<string, unknown> });

        try {
          const result = await mutate({
            variables: { tenantId, [idFieldName]: id, input },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            optimisticResponse: optimisticUpdates
              ? ({
                  __typename: 'Mutation',
                  [`update${entityName}`]: {
                    __typename: entityName,
                    id,
                    ...input,
                  },
                } as any)
              : undefined,
          });

          const entity = result.data as TEntity;
          trace.success(id);
          return entity;
        } catch (err) {
          trace.failure(err as Error, id);
          throw err;
        }
      };

      return { update, loading, error };
    }, [tenantId, logger, idFieldName, optimisticUpdates]);

    /**
     * Delete mutation with tenant injection
     */
    const useDelete = useCallback(() => {
      if (shouldUseMockData()) {
        return {
          delete: async () => {
            await new Promise((r) => setTimeout(r, 300));
            return true;
          },
          loading: false,
          error: undefined,
        };
      }

      if (!deleteMutation) {
        return {
          delete: async () => {
            throw new Error(`Delete mutation not configured for ${entityName}`);
          },
          loading: false,
          error: undefined,
        };
      }

      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [mutate, { loading, error }] = useMutation<{ success: boolean }>(deleteMutation);

      const deleteEntity = async (id: string): Promise<boolean> => {
        const trace = logger.trace('delete', { id });

        try {
          const result = await mutate({
            variables: { tenantId, [idFieldName]: id },
            update: (cache) => {
              // Remove from cache
              cache.evict({ id: cache.identify({ __typename: entityName, id }) });
              cache.gc();
            },
          });

          trace.success(id);
          return result.data?.success ?? true;
        } catch (err) {
          trace.failure(err as Error, id);
          throw err;
        }
      };

      return { delete: deleteEntity, loading, error };
    }, [tenantId, logger, idFieldName]);

    return {
      useList,
      useGet,
      useCreate,
      useUpdate,
      useDelete,
      entityName,
    };
  };
}

export default createRepository;
